import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Link, useTheme } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useForm } from 'react-hook-form';
import { AutocompleteElement, FormContainer, SelectElement, TextFieldElement } from 'react-hook-form-mui';
import { useNavigate } from 'react-router-dom';
import {useMutation, useQueryClient} from "@tanstack/react-query";

import PhoneMask from '@components/common/PhoneMask';
import RadioButtonGroup from '@components/common/HookFormMui/RadioButtonGroup';
import { useConfirm } from '@components/common/hooks/useConfirm';
import { counties, states, userOptions } from '@lib/constants';
import UserContext from '@lib/contexts/userContext';
import { PageContext } from '@lib/contexts/pageContext';
import { simpTextAddContact, simpTextAddUnsub, simpTextRemoveUnsub } from '@lib/api';
import {API_URLS, axiosRequest} from "@lib/axios";
import {scrollToAlert, tenDigit, toOptions} from '@lib/utils';

const Profile = () => {
  const navigate = useNavigate();
  const {userRecord, setUserRecord} = useContext(UserContext);
  const {setPageMessage, setPageMessages, setPageErrorMessage} = useContext(PageContext);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const theme = useTheme();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userRecord) {
      console.warn('No user record, redirecting to login');
      navigate('/login?redirect=/member/profile');
    }
  });

  const defaultValues = userRecord || {};
  Object.keys(defaultValues).forEach((key) => {
    if (defaultValues[key] === 'null') {
      defaultValues[key] = null;
    }
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: {
      errors,
      submitCount,
      isValidating,
    }
  } = useForm({
    defaultValues: {
      ...userRecord,
      household_size: `${userRecord.household_size}`,
      is_eligible: userRecord.is_eligible !== null ? userRecord.is_eligible.toString() : null,
    },
  });

  const stateValue = watch('state', null);

  useEffect(() => {
    // When state is Illinois we switch to a dropdown list of counties.
    // If the county is not in that list, then clear it to force the user to update county.
    if (stateValue?.id === 'IL') {
      const county = getValues('county');
      if (!counties.includes(county)) {
        setValue('county', '');
      }
    }
  }, [getValues, setValue, stateValue]);

  const hasErrors = errors && Object.keys(errors).length > 0;

  useEffect(() => {
    if (hasErrors) {
      document.getElementsByClassName('page-title')[0].scrollIntoView({ behavior: 'smooth' });
    }
  }, [hasErrors, submitCount]);

  useEffect(() => {
    setWasSubmitted(true);
  }, [submitCount]);

  useEffect(() => {
    if (isValidating && wasSubmitted) {
      setWasSubmitted(false);
    }
  }, [isValidating, wasSubmitted, setWasSubmitted]);

  useEffect(() => {
    if (isValidating) {
      setPageMessage(null);
    }
  }, [isValidating, setPageMessage]);

  const updateProfileMutation = useMutation({
    mutationFn: async (updates) => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.AUTH}/profile`,
      data: updates,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['staffUsersWithCurrentUser'] });
    },
    onError: async (err) => {
      setPageErrorMessage('Error saving profile data', err);
      console.log('[Profile][updateProfile] error:', err);
    },
  });

  const onSubmit = async (values) => {
    setPageMessage('');
    setPageMessages([]);
    setPageErrorMessage('');
    if (!values.phone_number.match(/[0-9]{10}/)) {
      return false;
    }
    const messages = [];
    setSubmitting(true);

    const data = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'first_name':
        case 'last_name':
        case 'street_address':
        case 'city':
        case 'zip_code':
          acc[key] = value ? value.trim() : value;
          break;
        case 'phone_number': {
          // Phone number must be formatted with country code, ie: '+15551234567'
          let phoneDigits = value.replaceAll(/[^0-9]/g, '');
          if (phoneDigits && phoneDigits.length === 10) {
            phoneDigits = `+1${phoneDigits}`;
          }
          acc[key] = phoneDigits;
          break;
        }
        case 'county':
        case 'state':
        case 'age_range':
        case 'race':
        case 'ethnicity':
        case 'gender':
        case 'household_size':
          acc[key] = value?.id || value;
          break;
        case 'is_eligible':
          acc[key] = values[key] === 'true';
          break;
        default: break;
      }
      return acc;
    }, {});

    if (userRecord.allow_text_program && userRecord.phone_number !== data.phone_number) {
      // Ask for re-enrollment to text program
      const enroll = await confirm({
        title: '',
        description: 'You are currently enrolled to receive text messages for the Healthy Text Program. Do you want to enroll your updated phone number to receive Healthy Text Program messages as well?',
        confirmationButtonProps: {
          variant: 'contained',
        },
        confirmationText: 'Yes',
        cancellationText: 'No',
      })
        .then(() => true)
        .catch(() => false);

      if (enroll) {
        try {
          // Add the old number to the unsubscribe list
          await simpTextAddUnsub({ phone: tenDigit(userRecord.phone_number) });
          // Remove the new number from the unsubscribe list (if it happened to be there)
          await simpTextRemoveUnsub({ phone: tenDigit(data.phone_number) });
          // Add contact to the list (subscribe)
          await simpTextAddContact({
            phone: tenDigit(data.phone_number),
            group: data.county,
            firstName: data.first_name,
            lastName: data.last_name,
            email: data.email,
          });
          messages.push('You have been enrolled to receive Healthy Text Program messages.');
          setPageMessages([...messages]);
        } catch (err) {
          console.error('SimpText sub error', err);
          // Update the enrollment status to NOT enrolled since we weren't able to reliably subscribe the new number
          data.allow_text_program = false;
          setPageErrorMessage('We were unable to enroll your new phone number to receive Healthy Text Program messages due to technical difficulties. Please try again later.');
          scrollToAlert();
        }
      } else {
        try {
          // Add the old number to the unsubscribe list
          await simpTextAddUnsub({ phone: tenDigit(userRecord.phone_number) });
        } catch (err) {
          console.error('SimpText unsub error', err);
        } finally {
          // Update the enrollment status to NOT enrolled since the new number won't be subscribed even if the old number
          // wasn't successfully unsubscribed.
          data.allow_text_program = false;
        }
      }
    }

    try {
      const userRecordResponse = await updateProfileMutation.mutateAsync(data);
      if (userRecordResponse) {
        setUserRecord(userRecordResponse);
        messages.push('Profile Updated');
        setPageMessages([...messages]);
        scrollToAlert()
      }
      setSubmitting(false);
      return userRecordResponse;
    } catch (error) {
      setSubmitting(false);
      setPageErrorMessage(error);
      scrollToAlert();
    }
  };

  useEffect(() => {
    // setting init values for custom handled fields
    if (userRecord && Object.keys(userRecord).length > 0) {
      Object.keys(userRecord).forEach((key) => {
        if (userRecord[key] === 'undefined' || userRecord[key] === 'null') {
          userRecord[key] = null;
        }
      });
      const {
        first_name,
        last_name,
        email,
        street_address,
        city,
        zip_code,
        county,
        phone_number,
        gender,
        is_eligible
      } = userRecord;
      let state = userRecord.state;

      setValue('first_name', first_name);
      setValue('last_name', last_name);
      setValue('email', email);
      setValue('street_address', street_address);
      setValue('city', city);
      setValue('zip_code', zip_code);
      setValue('county', county);
      setValue('gender', gender);

      setValue('phone_number', phone_number?.replace(/^\+1/, ''));

      // State must be abbreviated, ie: 'IL'
      if (state && state.length > 2) {
        state = states.find(({name}) => name === state).abbreviation;
      }
      setValue('state', state);

      setValue('is_eligible', is_eligible !== null ? is_eligible.toString() : null);
    }
  }, [userRecord, setValue]);

  const getErrors = () => {
    const messages = [];
    let hasRequiredError = false;
    Object.values(errors).forEach((err) => {
      if (err.type !== 'required') {
        messages.push(err.message);
      } else if (!hasRequiredError && wasSubmitted) {
        messages.push('Please fill out all required (*) fields.');
        hasRequiredError = true;
      }
    });
    return messages;
  };

  const errorMessages = getErrors();

  return (
    <div className={'form'} id={'profile-form'}>
      {!!errorMessages.length && errorMessages.map((err, i) =>
        <Alert severity="error" sx={{ marginBottom: '20px' }} key={`err${i}`}>
          {err}
        </Alert>
      )}
      <FormContainer onSuccess={handleSubmit(onSubmit)} FormProps={{className: 'mui'}}>
        <Grid container spacing={2}>
          <Grid xs={12} sm={8}>
            <TextFieldElement
              id="field-first_name"
              name="first_name"
              label={'First Name'}
              control={control}
              required
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <TextFieldElement
              id="field-last_name"
              name="last_name"
              label={'Last Name'}
              control={control}
              required
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <TextFieldElement
              id="field-street_address"
              name="street_address"
              label={'Street Address'}
              control={control}
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <TextFieldElement
              id="field-city"
              name="city"
              label={'City/Town'}
              control={control}
              required
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <SelectElement
              id="field-state"
              name="state"
              label={'State'}
              control={control}
              required
              fullWidth
              options={states.map(item => ({id: item.abbreviation, label: item.name}))}
            />
          </Grid>
          <Grid xs={12} sm={2}>
            <TextFieldElement
              id="field-zip"
              name="zip_code"
              label={'Zip Code'}
              control={control}
              required
              fullWidth
            />
          </Grid>
          <Grid xs={12} sm={8}>
            {stateValue === 'IL' ? (
              <AutocompleteElement
                name="county"
                label="County"
                control={control}
                required
                fullWidth
                options={toOptions(counties)}
                autocompleteProps={{
                  id: 'field-county',
                }}
              />
            ) : (
              <TextFieldElement
                id="field-county"
                name="county"
                label={'County'}
                control={control}
                required
                fullWidth
              />
            )}
          </Grid>
          <Grid xs={12} sm={8}>
            <TextFieldElement
              id="field-email"
              name="email"
              type={'email'}
              label={'Email'}
              control={control}
              disabled={true}
              fullWidth
              helperText={
                <>
                  (To update your email address, please contact <Link href="mailto:eatmovesave@illinois.edu">eatmovesave@illinois.edu</Link>)
                </>
              }
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <TextFieldElement
              id="field-phone_number"
              name="phone_number"
              type={'text'}
              label={'Cell Phone Number'}
              control={control}
              validation={{
                validate: {
                  tenDigits: (value) => value.replaceAll(/[^0-9]+/g, '').length === 10 || 'Phone number must be 10 digits',
                },
              }}
              InputProps={{
                inputComponent: PhoneMask,
              }}
              required
              fullWidth
            />
          </Grid>
          <Grid xs={10} id="field-is_eligible">
            <RadioButtonGroup
              name={'is_eligible'}
              label={'Are you eligible to participate in any of the following programs?'}
              formLabelProps={{
                focused: false,
              }}
              row={true}
              description={(error) => (
                <ul style={{
                  listStyleType: 'none',
                  columns: 2,
                  paddingLeft: 0,
                  margin: 0,
                  color: error ? theme.palette.error.main : '#000'
                }}>
                  <li>SNAP/LINK</li>
                  <li>WIC</li>
                  <li>Free or reduced-price school lunch or breakfast</li>
                  <li>Free summer meals</li>
                  <li>Medicaid</li>
                  <li>Head start</li>
                  <li>Food pantries</li>
                  <li>Affordable housing or shelters</li>
                </ul>
              )}
              helperTextProps={{
                sx: {
                  marginRight: 0,
                  marginLeft: 0,
                  marginTop: 0,
                  color: '#000',
                },
              }}
              options={[
                {id: 'true', label: 'Yes'},
                {id: 'false', label: 'No'},
              ]}
              control={control}
              required
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <AutocompleteElement
              name="household_size"
              label={'Household Size'}
              control={control}
              fullWidth
              options={toOptions(userOptions.householdSize)}
              autocompleteProps={{
                id: 'field-household_size',
              }}
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <AutocompleteElement
              name="gender"
              label={'Gender'}
              control={control}
              fullWidth
              options={toOptions(userOptions.gender)}
              autocompleteProps={{
                id: 'field-gender',
              }}
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <AutocompleteElement
              name="ethnicity"
              label={'Ethnicity'}
              control={control}
              fullWidth
              options={toOptions(userOptions.ethnicity)}
              autocompleteProps={{
                id: 'field-ethnicity',
              }}
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <AutocompleteElement
              name="race"
              label={'Race'}
              control={control}
              fullWidth
              options={toOptions(userOptions.race)}
              autocompleteProps={{
                id: 'field-race',
              }}
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <AutocompleteElement
              name="age_range"
              label={'Age Range'}
              control={control}
              fullWidth
              options={toOptions(userOptions.ageRange)}
              autocompleteProps={{
                id: 'field-age_range',
              }}
            />
          </Grid>
          <Grid xs={12} sm={8}>
            <Button
              id="button-submit"
              disabled={submitting}
              type={'submit'}
              variant="contained"
              size="small"
              sx={{ marginTop: '20px' }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </FormContainer>
    </div>
  );
};

export default Profile;
